import { Injectable } from '@angular/core';
import { State } from 'app/core/state';
import {
    CDFT,
    CDFTHeader,
    CdftAnnulusPressureReport,
    CdftControlLineTest,
    CdftRecord,
    CdftSssvDbt,
    CdftSubsidenceMonitoring,
    CdftXmtWhvTest,
    Response,
} from '../model';
import { toFormData } from 'app/core/shared/helper/url.helper';

@Injectable({ providedIn: 'root' })
export class CDFTState extends State<CDFT> implements CDFT {
    id: string;
    result: any;
    status: string;
    wfamStatus: string;
    date: string;
    preparedBy: string;
    reviewedBy: string;
    approvedBy: string;
    approvedDate: string;
    reviewedDate: string;
    remarks: string;
    overallRemarks: string;
    cdftHeader: CDFTHeader;
    cdftXmtWhvTest: CdftXmtWhvTest[];
    cdftSssvDbt: CdftSssvDbt;
    cdftAnnulusPressureReport: CdftAnnulusPressureReport;
    cdftControlLineTest: CdftControlLineTest[];
    cdftSubsidenceMonitoring: CdftSubsidenceMonitoring;

    _formGroup = {
        cdftHeader: null,
        cdftXmtWhvTest: null,
        cdftSssvDbt: null,
        cdftAnnulusPressureReport: null,
        cdftControlLineTest: null,
        cdftSubsidenceMonitoring: null,
        detail: null,
    };

    constructor() {
        super();
    }

    storeResolver(response: Response<CDFT>) {
        return {
            ...response.data,
            cdftHeader: {
                ...response.data.cdftHeader,
                date: response.data.cdftHeader.date,
                treecapConnRemarks: response.data.cdftHeader.treecapConnRemarks,
                treecapCondRemarks: response.data.cdftHeader.treecapCondRemarks,
            },
        };
    }

    setFormGroup(key, formGroup) {
        this._formGroup[key] = formGroup;
    }

    toPayload() {
        const data = this.toPayloadObject();
        const formData: any = toFormData(data);
        // formData.forEach((value, key) => {
        //     console.log(`${key}: ${value}`);
        // });
        return formData;
    }

    toPayloadObject() {
        return {
            ...this.toCdft(),
            cdftHeader: this.toCdftHeader(),
            cdftXmtWhvTest: this.toCdftXmtWhvTest(),
            cdftSssvDbt: this._formGroup.cdftSssvDbt?.getRawValue(),
            cdftAnnulusPressureReport:
                this._formGroup.cdftAnnulusPressureReport?.getRawValue(),
            cdftControlLineTest:
                this._formGroup.cdftControlLineTest?.getRawValue(),
            cdftSubsidenceMonitoring:
                this._formGroup.cdftSubsidenceMonitoring?.getRawValue(),
        };
    }

    toCdft(): CdftRecord {
        const {
            id,
            date,
            preparedBy,
            reviewedBy,
            approvedBy,
            approvedDate,
            reviewedDate,
            remarks,
            overallRemarks,
            status,
            wfamStatus,
        } = this;
        return {
            id,
            result: this.result,
            status,
            date,
            preparedBy,
            reviewedBy,
            approvedBy,
            approvedDate,
            reviewedDate,
            wfamStatus,
            remarks,
            overallRemarks
        };
    }

    toCdftHeader(): CDFTHeader {
        const raw = this._formGroup.cdftHeader?.getRawValue();
        let date = raw.date;
        try {
            date = raw.date?.toSQLDate();
        } catch (e) { }

        return {
            ...raw,
            date,
            cdftAttachments: this._formGroup.cdftHeader
                .get('cdftAttachments')
                .getRawValue()
                .map((row) => {
                    const value = {};
                    for (const key in row) {
                        value[key] = row[key].value;
                    }
                    return value;
                }),
        };
    }

    toCdftXmtWhvTest(): CdftXmtWhvTest[] {
        const cdftXmtWhvTest: CdftXmtWhvTest[] = [];
        const value = this._formGroup.cdftXmtWhvTest?.getRawValue();
        for (const key in value) {
            const row = value[key];

            const cdftXmtWhvGreasing = { ...row.cdftXmtWhvGreasing };
            row.cdftXmtWhvGreasing = [];
            for (const key in cdftXmtWhvGreasing) {
                row.cdftXmtWhvGreasing.push(cdftXmtWhvGreasing[key]);
            }
            cdftXmtWhvTest.push(row);
        }

        return cdftXmtWhvTest;
    }

    recalculate() {
        // Check wellhead valve conditions first
        const wellheadValveResults = this.getWellheadValveResults();

        if (wellheadValveResults.some((result) => result === 'FAIL')) {
            this.result = 'Failed';
            return;
        }

        // Check SSSV result
        const sssvResult = this.getSssvResult();

        if (sssvResult === 'FAILED' || sssvResult === 'UTT') {
            this.result = 'Failed';
            return;
        }

        // If no failures, determine result based on wellhead valves
        if (wellheadValveResults.every((result) => result === 'NA')) {
            this.result = '-';
        } else if (
            wellheadValveResults.some(
                (result) => result !== 'NA' && result !== 'FAIL')
        ) {
            const result = wellheadValveResults.slice(0, 5).some((result) => result === 'UTT')
            this.result = result ? 'Failed' : 'Passed';
        } else {
            this.result = 'Failed';
        }
    }

    private getWellheadValveResults(): string[] {
        const valveFormGroup = this._formGroup.cdftXmtWhvTest;
        if (!valveFormGroup) return [];

        const valveKeys = [
            'wValve',
            'cValve',
            'ssValve',
            'lmValve',
            'kwValve',
            'annulusA',
            'annulusB',
            'annulusC',
            'annulusD',
        ];
        return valveKeys.map((key) => {
            const valve = valveFormGroup.get(key);
            return valve?.get('cdftXmtWhvGreasing.after.result')?.value || 'UTT';
        });
    }

    private getSssvResult(): string {
        const sssvFormGroup = this._formGroup.cdftSssvDbt;
        return sssvFormGroup?.get('sssvResult')?.value || 'PASSED';
    }
}
